import { isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Filter } from 'components/molecules/filters/types';
import { defaultFilter } from 'components/molecules/filters/utils';
import { getFilterTransformer } from 'components/pages/filter-transformer';
import { useSearch } from 'hooks/use-search';
import type { Content } from 'stores/core';
import { queueMacrotask } from 'utils/macrotask';
import type { FilterableField } from './fields';
import type { FilterFunction } from './filter-functions/types';
import { FieldFilter } from './filters';
import { getFilterFunction } from './get-filter-function';

interface Filters {
  selectFilter: (next: Filter) => void;
  replaceFilter: (next: Filter) => void;
  clearFilters: () => void;
  filter: Filter;
  filterFunction: FilterFunction | null;
  lookup: Record<string, FilterableField>;
}

export const useFilters = (
  lookup: Record<string, FilterableField>,
  status: Content<any>[0],
): Filters => {
  const transformer = useMemo(() => getFilterTransformer(lookup), [lookup]);
  const [search, setSearch] = useSearch(transformer);

  const [filter, setFilter] = useState(search);
  const selectFilter = useCallback(
    (new_filter: Filter) => {
      if (status === 'loading') {
        return;
      }
      /* TODO:: This approach of setting two state is not scalable. We need to migrate to single state directly from URL. */
      setFilter(new_filter);
      setSearch(new_filter);
    },
    [status, setFilter, setSearch],
  );
  const replaceFilter = useCallback(
    (new_filter: Filter) => {
      setFilter(new_filter);
      queueMacrotask(() => setSearch(new_filter, true));
    },
    [setFilter, setSearch],
  );
  const filterFunction = useMemo(() => getFilterFunction(filter), [filter]);
  const clearFilters = useCallback(() => {
    selectFilter(defaultFilter);
  }, [selectFilter]);

  useEffect(() => {
    if (
      !isEqual(
        {
          type: search.type,
          criteria: search.criteria,
        },
        {
          type: filter.type,
          criteria: filter.criteria.filter(FieldFilter.isValid),
        },
      )
    ) {
      setFilter(search);
    }
  }, [search, filter, setFilter]);

  return {
    selectFilter,
    replaceFilter,
    clearFilters,
    filter,
    filterFunction,
    lookup,
  };
};
